import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { UtilisationMetrics } from '@/types/utilisation';

interface EmployeePerformanceSectionProps {
  employeeMetrics: UtilisationMetrics[];
  onEmployeeClick?: (employee: UtilisationMetrics) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const EmployeePerformanceSection: React.FC<EmployeePerformanceSectionProps> = ({
  employeeMetrics,
  onEmployeeClick,
}) => {
  // Sort employees by utilisation rate (highest first)
  const sortedMetrics = [...employeeMetrics].sort((a, b) => b.utilisationRate - a.utilisationRate);

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-base">Team Utilisation</CardTitle>
        <CardDescription>Individual utilisation metrics</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <ScrollAreaViewport className="h-full pr-4">
            <div className="space-y-4">
              {sortedMetrics.map((metric) => (
                <div 
                  key={metric.employeeId} 
                  className="space-y-2 pb-4 border-b last:border-0 cursor-pointer hover:bg-muted/50 rounded-lg p-2 -m-2 transition-colors"
                  onClick={() => onEmployeeClick?.(metric)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium block truncate">{metric.employeeName}</span>
                      <span className="text-xs text-muted-foreground">{metric.department}</span>
                    </div>
                    <Badge variant="secondary" className="ml-2 shrink-0">
                      {metric.projectCount} {metric.projectCount === 1 ? 'project' : 'projects'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Utilisation</span>
                      <span className="font-medium">
                        {(metric.utilisationRate * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all',
                          metric.utilisationRate >= 0.75
                            ? 'bg-success-500'
                            : metric.utilisationRate >= 0.5
                              ? 'bg-warning-500'
                              : 'bg-error-500'
                        )}
                        style={{ width: `${metric.utilisationRate * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Total: </span>
                      <span className="font-medium">{metric.totalHours.toFixed(1)}h</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Billable: </span>
                      <span className="font-medium text-success-600 dark:text-success-400">
                        {metric.billableHours.toFixed(1)}h
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Non-Bill: </span>
                      <span className="font-medium">{metric.nonBillableHours.toFixed(1)}h</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Target: </span>
                      <span className="font-medium">{metric.targetHours.toFixed(1)}h</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
